/**
 * @fileoverview Client-side launch gate for the Locus conversational overlay
 * (FRE-1229 → FRE-1231 → FRE-1270).
 *
 * Gates the global `LocusRuntime` mount behind the PostHog feature flag
 * `locus-runtime` so the mount can ship **dark**: `develop` deploys straight
 * to production, and this flag is what keeps real users from seeing Locus
 * until the staged rollout (FRE-1231) turns it on — no code change to flip it.
 *
 * **FRE-1270 — consent-independent evaluation.** The signed-in overlay reads
 * the flag via {@link useLocusRuntimeFlag}, which hits the BFF
 * (`GET /api/feature-flags/locus`) so evaluation no longer depends on client
 * PostHog initializing after analytics consent (FRE-809). Client-side
 * {@link useLocusRuntimeEnabledFor} / {@link isLocusRuntimeEnabledFor} remain
 * for tests and any surface that still wants the consent-gated path; the
 * mount and feed Plan button use the server-evaluated hook.
 *
 * **Staged rollout mechanics (FRE-1231).** The rollout is staged per PRD §9
 * Phase 4 route group — `(app)` → `(dynamic)` → `(city)` — via a single flag
 * carrying a JSON payload rather than one flag per surface:
 *
 * ```json
 * { "surfaces": ["app"] }
 * ```
 *
 * A route group is live iff BOTH hold:
 *   1. the flag evaluates `enabled: true` (the percentage-rollout lever), AND
 *   2. the flag's payload is a JSON object whose `surfaces` array contains
 *      the route group's exact lowercase name.
 *
 * **Fail closed** at every layer: flag undefined / unavailable / `false` /
 * BFF error / missing PostHog config ⇒ off everywhere; payload missing,
 * malformed, or lacking the route group ⇒ off for that route group.
 */
'use client';

import { useEffect, useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';

import { getFlagPayload, isFlagEnabled, useFlagEnabled, useFlagPayload } from './flagGate';
import {
  isLocusRuntimeEvaluationEnabledFor,
  LOCUS_ROLLOUT_ROUTE_GROUPS,
  LOCUS_RUNTIME_DISABLED,
  LOCUS_RUNTIME_FLAG,
  type LocusRolloutRouteGroup,
  type LocusRuntimeEvaluation,
  parseLocusRolloutSurfaces,
} from './locusRuntimeShared';

export {
  isLocusRuntimeEvaluationEnabledFor,
  LOCUS_ROLLOUT_ROUTE_GROUPS,
  LOCUS_RUNTIME_DISABLED,
  LOCUS_RUNTIME_FLAG,
  parseLocusRolloutSurfaces,
};
export type { LocusRolloutRouteGroup, LocusRuntimeEvaluation };

function isRolloutRouteGroup(value: string): value is LocusRolloutRouteGroup {
  return (LOCUS_ROLLOUT_ROUTE_GROUPS as readonly string[]).includes(value);
}

/**
 * Whether the Locus runtime is enabled for the current visitor on the given
 * route group via **client** PostHog (consent-gated). Prefer
 * {@link useLocusRuntimeFlag} + {@link isLocusRuntimeEvaluationEnabledFor}
 * for the overlay mount (FRE-1270).
 */
export function isLocusRuntimeEnabledFor(routeGroup: string): boolean {
  if (!isRolloutRouteGroup(routeGroup)) return false;
  if (!isFlagEnabled(LOCUS_RUNTIME_FLAG)) return false;
  return parseLocusRolloutSurfaces(getFlagPayload(LOCUS_RUNTIME_FLAG)).has(routeGroup);
}

/**
 * React hook wrapping {@link isLocusRuntimeEnabledFor} that re-renders when
 * the flag or payload state changes (flag reloads, late PostHog init after
 * consent). Starts fail-closed (`false`) and upgrades once the flag resolves
 * `true` with a payload covering the route group.
 *
 * @deprecated For overlay mounting use {@link useLocusRuntimeFlag} (FRE-1270)
 * so consent-decliners are not denied the feature.
 */
export function useLocusRuntimeEnabledFor(routeGroup: string): boolean {
  const flagEnabled = useFlagEnabled(LOCUS_RUNTIME_FLAG);
  const payload = useFlagPayload(LOCUS_RUNTIME_FLAG);
  if (!isRolloutRouteGroup(routeGroup)) return false;
  if (!flagEnabled) return false;
  return parseLocusRolloutSurfaces(payload).has(routeGroup);
}

/**
 * Parse the BFF JSON body into a fail-closed {@link LocusRuntimeEvaluation}.
 * Exported for unit tests.
 */
export function parseLocusRuntimeFlagResponse(data: unknown): LocusRuntimeEvaluation {
  if (typeof data !== 'object' || data === null) {
    return LOCUS_RUNTIME_DISABLED;
  }
  const record = data as Record<string, unknown>;
  const enabled = record.enabled === true;
  if (!enabled) {
    return LOCUS_RUNTIME_DISABLED;
  }
  const surfaces = [...parseLocusRolloutSurfaces({ surfaces: record.surfaces })];
  return { enabled: true, surfaces };
}

/**
 * Server-evaluated `locus-runtime` flag (FRE-1270). Fetches
 * `GET /api/feature-flags/locus` when the viewer has a resolved user id;
 * anonymous viewers stay fail-closed without a network call. Independent of
 * analytics consent / client PostHog init.
 *
 * Starts `{ enabled: false, surfaces: [] }` on SSR and first paint so
 * hydration always agrees on "not mounted", then upgrades once the BFF
 * responds.
 */
export function useLocusRuntimeFlag(): LocusRuntimeEvaluation {
  const { user } = useAuth();
  const userId = typeof user?.id === 'string' && user.id.length > 0 ? user.id : null;
  const [state, setState] = useState<LocusRuntimeEvaluation>(LOCUS_RUNTIME_DISABLED);

  useEffect(() => {
    if (userId === null) {
      setState(LOCUS_RUNTIME_DISABLED);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    void fetch('/api/feature-flags/locus', {
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (res): Promise<LocusRuntimeEvaluation> => {
        if (!res.ok) return LOCUS_RUNTIME_DISABLED;
        const data: unknown = await res.json();
        return parseLocusRuntimeFlagResponse(data);
      })
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch(() => {
        if (!cancelled) setState(LOCUS_RUNTIME_DISABLED);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId]);

  return state;
}
